<template>
  <v-container>
    <v-row v-if="isLoading">
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>
    <v-fade-transition>
      <div v-if="!isLoading && catalog">
        <!-- The app-bar breadcrumb already labels this page "Módulos", so an
             in-page heading would duplicate it. Kept screen-reader-only so the
             modules region still has an accessible label without the visual
             repeat. ("Planos" below stays visible — it's a sub-section the
             breadcrumb doesn't name.) -->
        <h2 class="d-sr-only">Módulos</h2>
        <!-- Modules collapse to their header by default to keep the catalog
             scannable; this flips every module's detail open or shut at once. -->
        <div v-if="expandableKeys.length" class="d-flex justify-end mb-2">
          <v-btn
            variant="text"
            size="small"
            :prepend-icon="
              allExpanded ? mdiUnfoldLessHorizontal : mdiUnfoldMoreHorizontal
            "
            @click="toggleAll"
          >
            {{ allExpanded ? "Recolher tudo" : "Expandir tudo" }}
          </v-btn>
        </div>
        <v-expansion-panels
          v-model="openPanels"
          variant="accordion"
          flat
          multiple
          class="modules"
        >
          <v-expansion-panel
            v-for="mod in catalog.modules"
            :key="mod.key"
            :value="mod.key"
            :readonly="!hasDetails(mod)"
            :class="{ 'recently-changed': recentKey === `module:${mod.key}` }"
          >
            <!-- Menu-initiated module mutations (suspend/revoke/trial) have no
                 button of their own to carry a spinner, so a bar pinned to the
                 panel top stands in — same affordance as the feature rows. -->
            <v-progress-linear
              v-if="pendingKey === `module:${mod.key}`"
              class="module-progress"
              indeterminate
              color="primary"
            />
            <!-- hide-actions drops the expand chevron on modules with no detail
                 to open; readonly (above) keeps them from toggling at all. -->
            <v-expansion-panel-title
              :hide-actions="!hasDetails(mod)"
              class="px-4"
            >
              <div class="flex-grow-1">
                <div class="d-flex align-center ga-2 flex-wrap">
                  <h3 class="text-h6">
                    {{ mod.title }}
                  </h3>
                  <!-- Module kind as a quiet marker after the title: cube = Base
                       (core), puzzle = Add-on. A text-variant chip (not a filled
                       pill) keeps it below the status chip — this is
                       classification, not state. Base is muted as the
                       unremarkable default; add_ons keep the primary tint as the
                       commercial unit. -->
                  <v-chip
                    size="small"
                    variant="text"
                    :class="[
                      'px-1',
                      mod.kind === 'core' ? 'text-medium-emphasis' : '',
                    ]"
                    :color="mod.kind === 'core' ? undefined : 'primary'"
                    :prepend-icon="
                      mod.kind === 'core' ? mdiCubeOutline : mdiPuzzleOutline
                    "
                  >
                    {{ mod.kind === "core" ? "Base" : "Add-on" }}
                  </v-chip>
                </div>
                <div class="text-body-2 text-medium-emphasis mt-1">
                  {{ mod.description }}
                  <span
                    v-if="moduleStatus(mod) === 'expired'"
                    class="text-warning"
                  >
                    — o período de trial terminou; conceda o módulo para
                    reativar.
                  </span>
                </div>
                <div
                  v-if="moduleMeta(mod)"
                  class="text-caption text-medium-emphasis mt-1"
                >
                  {{ moduleMeta(mod) }}
                </div>
                <!-- A muted count of what the module holds — what's tucked away
                     while collapsed, a quiet section count once open. Always
                     rendered (not gated on the collapsed state) so expanding
                     doesn't drop a line from the header and flicker the panel as
                     the body animates open. -->
                <div
                  v-if="hasDetails(mod)"
                  class="text-caption text-medium-emphasis mt-1"
                >
                  {{ detailSummary(mod) }}
                </div>
              </div>
              <div class="d-flex align-center ga-3 flex-shrink-0 ms-3">
                <!-- Status chip at the row's end — the single loud signal for
                     the module's lifecycle state. -->
                <v-chip
                  :color="stateDisplay(moduleStatus(mod)).color"
                  size="small"
                >
                  {{ stateDisplay(moduleStatus(mod)).label }}
                </v-chip>
                <!-- Reserved action gutter, mirroring the feature rows. core
                     modules (nfce_core/nfce_gaps) have no master grant — any
                     module-level write 409s — so they render no controls, but
                     the empty gutter keeps their status chip in the same column
                     as the add_on modules (which carry the overflow menu) and
                     the feature rows below. @click.stop so these controls (and
                     the overflow menu) act without toggling the panel. -->
                <div
                  class="module-actions d-flex align-center justify-end ga-1 flex-shrink-0"
                  @click.stop
                >
                  <template
                    v-if="$can('company:update') && mod.kind === 'add_on'"
                  >
                    <v-btn
                      v-if="!mod.granted"
                      size="small"
                      color="primary"
                      variant="tonal"
                      :loading="pendingKey === `module:${mod.key}`"
                      :disabled="!!pendingKey"
                      @click="grantModule(mod)"
                    >
                      Conceder
                    </v-btn>
                    <v-btn
                      v-else-if="mod.state === 'suspended'"
                      size="small"
                      color="primary"
                      variant="tonal"
                      :loading="pendingKey === `module:${mod.key}`"
                      :disabled="!!pendingKey"
                      @click="resumeModule(mod)"
                    >
                      Reativar
                    </v-btn>
                    <!-- Destructive/secondary verbs live in the overflow menu,
                         mirroring the feature rows; color stays on the menu item
                         and the confirm dialog, not on the section header. -->
                    <v-menu>
                      <template v-slot:activator="{ props: menuProps }">
                        <v-btn
                          icon
                          size="small"
                          density="comfortable"
                          variant="text"
                          v-bind="menuProps"
                          :disabled="!!pendingKey"
                          aria-label="Mais ações"
                        >
                          <v-icon :icon="mdiDotsVertical" />
                        </v-btn>
                      </template>
                      <v-list density="compact">
                        <v-list-item
                          v-if="!mod.granted"
                          title="Iniciar trial"
                          @click="openTrialDialog('module', mod.key, mod.title)"
                        />
                        <v-list-item
                          v-if="mod.granted && mod.state !== 'suspended'"
                          title="Suspender"
                          @click="confirmSuspendModule(mod)"
                        />
                        <v-list-item
                          v-if="mod.granted"
                          title="Revogar"
                          base-color="error"
                          @click="confirmRevokeModule(mod)"
                        />
                      </v-list>
                    </v-menu>
                  </template>
                </div>
              </div>
            </v-expansion-panel-title>

            <!-- Feature rows and quota rows are the module's detail, lazily
                 mounted by the panel and revealed only once it's expanded. -->
            <v-expansion-panel-text v-if="hasDetails(mod)">
              <template v-if="moduleFeatures(mod).length">
                <entitlement-feature-list
                  :features="moduleFeatures(mod)"
                  :pending-key="pendingKey"
                  :recent-key="recentKey"
                  :can-update="$can('company:update')"
                  :module-active="isModuleActive(mod)"
                  @grant-module="grantOwningModule"
                  @grant="grantFeature"
                  @trial="
                    openTrialDialog('feature', $event.feature, $event.description)
                  "
                  @suspend="confirmSuspendFeature"
                  @resume="resumeFeature"
                  @revoke="confirmRevokeFeature"
                />
              </template>

              <!-- Usage limits (quotas) ship grouped under their owning module,
                   so they render inside the module panel. A small label keeps
                   them distinct from the feature rows above. Every current quota
                   is internal — safe on this vendor-only surface. -->
              <template v-if="mod.quotas.length">
                <v-divider v-if="moduleFeatures(mod).length" />
                <div class="text-overline text-medium-emphasis px-4 pt-3">
                  Limites de uso
                </div>
                <entitlement-quota-list
                  :quotas="mod.quotas"
                  :pending-key="pendingKey"
                  :recent-key="recentKey"
                  :can-update="$can('company:update')"
                  @edit="openQuotaDialog"
                />
              </template>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Plans are the one spot a card fits: comparable bundles you pick one
             of, as bordered tiles in a grid. -->
        <h2 class="text-h5 mt-10 mb-4">Planos</h2>
        <v-row>
          <v-col
            v-for="plan in catalog.plans"
            :key="plan.plan_key"
            cols="12"
            md="6"
          >
            <v-card
              flat
              border
              class="h-100 d-flex flex-column"
              :class="{
                'recently-changed': recentKey === `plan:${plan.plan_key}`,
              }"
            >
              <v-card-title>{{ planLabel(plan.plan_key) }}</v-card-title>
              <v-card-subtitle>
                <code class="text-caption">{{ plan.plan_key }}</code>
              </v-card-subtitle>
              <v-card-text class="flex-grow-1">
                <div class="text-caption text-medium-emphasis mb-2">
                  Concessões do pacote
                </div>
                <v-chip
                  v-for="grant in plan.grants"
                  :key="grant"
                  size="small"
                  class="mr-1 mb-1"
                  variant="outlined"
                  :prepend-icon="grantIcon(grant)"
                >
                  {{ grantLabel(grant) }}
                </v-chip>
              </v-card-text>
              <v-card-actions v-if="$can('company:update')">
                <v-spacer />
                <v-btn
                  color="primary"
                  variant="tonal"
                  :loading="pendingKey === `plan:${plan.plan_key}`"
                  :disabled="!!pendingKey"
                  @click="confirmApplyPlan(plan)"
                >
                  Aplicar plano
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-fade-transition>

    <!-- Generic confirm dialog -->
    <v-dialog v-model="confirmDialog.open" max-width="480">
      <v-card>
        <v-card-title>{{ confirmDialog.title }}</v-card-title>
        <v-card-text>{{ confirmDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmDialog.open = false">
            Cancelar
          </v-btn>
          <v-btn
            :color="confirmDialog.color"
            variant="flat"
            @click="runConfirmedAction"
          >
            {{ confirmDialog.actionLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Trial dialog -->
    <v-dialog v-model="trialDialog.open" max-width="480">
      <v-card>
        <v-card-title>Iniciar trial</v-card-title>
        <v-card-subtitle class="text-wrap">
          {{ trialDialog.label }}
        </v-card-subtitle>
        <v-card-text>
          <v-menu
            v-model="startsMenu"
            :close-on-content-click="false"
            location="bottom"
          >
            <template v-slot:activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                :model-value="formatTrialDate(trialDialog.startsAt)"
                label="Inicia em (opcional)"
                readonly
                clearable
                hide-details="auto"
                class="mb-3"
                :prepend-inner-icon="mdiCalendar"
                :error-messages="trialStartsErrors"
                @click:clear="trialDialog.startsAt = null"
              />
            </template>
            <v-date-picker
              v-model="trialDialog.startsAt"
              hide-header
              show-adjacent-months
              @update:model-value="startsMenu = false"
            />
          </v-menu>
          <v-menu
            v-model="expiresMenu"
            :close-on-content-click="false"
            location="bottom"
          >
            <template v-slot:activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                :model-value="formatTrialDate(trialDialog.expiresAt)"
                label="Expira em *"
                readonly
                hide-details="auto"
                :prepend-inner-icon="mdiCalendar"
                :error-messages="trialExpiresErrors"
              />
            </template>
            <v-date-picker
              v-model="trialDialog.expiresAt"
              hide-header
              show-adjacent-months
              @update:model-value="expiresMenu = false"
            />
          </v-menu>
          <div class="d-flex align-center ga-2 mt-2">
            <span class="text-caption text-medium-emphasis">Atalho:</span>
            <v-chip
              v-for="days in TRIAL_SHORTCUTS"
              :key="days"
              size="small"
              variant="tonal"
              color="primary"
              @click="setTrialExpiresFromNow(days)"
            >
              {{ days }} dias
            </v-chip>
          </div>
          <div class="text-caption text-medium-emphasis mt-2">
            O trial requer uma data de expiração. A data de início é opcional e
            não pode ser posterior ao dia de expiração.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="trialDialog.open = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" variant="flat" @click="submitTrial">
            Iniciar trial
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Quota limit dialog -->
    <v-dialog v-model="quotaDialog.open" max-width="480">
      <v-card>
        <v-card-title>Definir limite</v-card-title>
        <v-card-text>
          <div class="text-body-2 text-medium-emphasis mb-4">
            {{ quotaDialog.quota?.description }}
          </div>
          <v-text-field
            v-model="quotaDialog.value"
            type="number"
            label="Limite"
            hide-details="auto"
            :error-messages="quotaValueErrors"
          />
        </v-card-text>
        <v-card-actions>
          <!-- Restore-to-default lives here now (instead of an inline row
               button): a tertiary, non-destructive verb on the quota's own
               editor, shown only when there's an override to undo. -->
          <v-btn
            v-if="quotaDialog.quota?.is_overridden"
            variant="text"
            @click="submitQuotaReset"
          >
            Restaurar padrão
          </v-btn>
          <v-spacer />
          <v-btn variant="text" @click="quotaDialog.open = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" variant="flat" @click="submitQuotaSet">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, inject, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import {
  mdiDotsVertical,
  mdiViewModule,
  mdiFlag,
  mdiCalendar,
  mdiCubeOutline,
  mdiPuzzleOutline,
  mdiUnfoldMoreHorizontal,
  mdiUnfoldLessHorizontal,
} from "@mdi/js";
import { format, startOfDay, endOfDay, isAfter, addDays } from "date-fns";
import { useSnackbar } from "@/plugins/snackbar";
import { useDateFormat } from "@/composables/useDateFormat";
import EntitlementFeatureList from "@/views/company/EntitlementFeatureList.vue";
import EntitlementQuotaList from "@/views/company/EntitlementQuotaList.vue";
import { planLabel, stateDisplay } from "@/views/company/entitlementDisplay";
import {
  useEntitlementsCatalogQuery,
  useModuleGrantMutation,
  useFeatureGrantMutation,
  useModuleSuspendMutation,
  useModuleResumeMutation,
  useFeatureSuspendMutation,
  useFeatureResumeMutation,
  usePlanApplyMutation,
  useQuotaSetMutation,
  useQuotaResetMutation,
} from "@/queries/modules/entitlements";

const route = useRoute();
const snackbar = useSnackbar();
const $can = inject("$can");
const { formatFromNow } = useDateFormat();

const tenantId = computed(() => {
  return route.params.tenant_id ? Number(route.params.tenant_id) : null;
});

const { data: catalog, isLoading, error } = useEntitlementsCatalogQuery(tenantId);

const moduleGrantMutation = useModuleGrantMutation(tenantId);
const featureGrantMutation = useFeatureGrantMutation(tenantId);
const moduleSuspendMutation = useModuleSuspendMutation(tenantId);
const moduleResumeMutation = useModuleResumeMutation(tenantId);
const featureSuspendMutation = useFeatureSuspendMutation(tenantId);
const featureResumeMutation = useFeatureResumeMutation(tenantId);
const planApplyMutation = usePlanApplyMutation(tenantId);
const quotaSetMutation = useQuotaSetMutation(tenantId);
const quotaResetMutation = useQuotaResetMutation(tenantId);

const pendingKey = ref(null);

// Every mutation replaces the whole catalog, so briefly flash the card/rows of
// the mutated key — otherwise nothing tells the operator what just changed.
const recentKey = ref(null);
let recentKeyTimer = null;
onBeforeUnmount(() => clearTimeout(recentKeyTimer));

const confirmDialog = ref({
  open: false,
  title: "",
  message: "",
  actionLabel: "Confirmar",
  color: "primary",
  action: null,
});

const trialDialog = ref({
  open: false,
  targetType: "module",
  targetKey: "",
  label: "",
  expiresAt: null,
  startsAt: null,
  showErrors: false,
});

// v-date-picker lives in a v-menu with a readonly activator, so the menu won't
// auto-close on selection — each menu's own open state lets us close it from
// the picker's @update:model-value.
const expiresMenu = ref(false);
const startsMenu = ref(false);

// Activator label for the readonly date fields; empty when unselected.
const formatTrialDate = (date) => (date ? format(date, "dd/MM/yyyy") : "");

// Common trial lengths offered as one-click chips next to the expiry field.
const TRIAL_SHORTCUTS = [7, 14, 30];

// Quick-set the expiry to N days from now; submit resolves it to end-of-day.
const setTrialExpiresFromNow = (days) => {
  trialDialog.value.expiresAt = addDays(new Date(), days);
};

const quotaDialog = ref({
  open: false,
  quota: null,
  value: 0,
  showErrors: false,
});

// Inline field errors appear after the first submit attempt and then clear
// reactively as the user fixes the input.
const trialExpiresErrors = computed(() => {
  if (!trialDialog.value.showErrors) return [];
  return trialDialog.value.expiresAt
    ? []
    : ["Informe a data de expiração do trial."];
});

const trialStartsErrors = computed(() => {
  if (!trialDialog.value.showErrors) return [];
  const { startsAt, expiresAt } = trialDialog.value;
  // Date-only: compare by calendar day. Same-day start+expire is valid (expiry
  // resolves to end-of-day at submit), so only a strictly later start day errors.
  return startsAt &&
    expiresAt &&
    isAfter(startOfDay(startsAt), startOfDay(expiresAt))
    ? ["A data de início não pode ser posterior ao dia de expiração."]
    : [];
});

// Quota limits are integers ≥ 0 (the backend rejects negatives / non-ints with 422).
const quotaValueErrors = computed(() => {
  if (!quotaDialog.value.showErrors) return [];
  const { value } = quotaDialog.value;
  const parsed = value === "" || value === null ? NaN : Number(value);
  return Number.isInteger(parsed) && parsed >= 0
    ? []
    : ["Informe um limite inteiro maior ou igual a zero."];
});

// The catalog arrives grouped: each module carries its own core `features` and
// in-module `addons`. The feature rows branch on classification, so render both
// kinds in one list per module.
const moduleFeatures = (mod) => [...mod.features, ...mod.addons];

// A module's detail (feature rows + quota rows) collapses behind its header by
// default, so the catalog reads as a scannable list of module headers. The
// expansion panels (multiple) track the open modules as an array of module
// keys; empty — the default — means every module is collapsed. The keys are
// stable across the catalog refetch each mutation triggers, so a panel the
// operator opened stays open afterwards.
const openPanels = ref([]);

// Only modules that actually carry feature/quota rows get a chevron; an empty
// module has nothing to expand.
const hasDetails = (mod) =>
  moduleFeatures(mod).length > 0 || mod.quotas.length > 0;

// Plural-aware summary of what's hidden, shown under the header while collapsed.
const detailSummary = (mod) => {
  const parts = [];
  const featureCount = moduleFeatures(mod).length;
  const quotaCount = mod.quotas.length;
  if (featureCount) {
    parts.push(
      `${featureCount} ${featureCount === 1 ? "funcionalidade" : "funcionalidades"}`,
    );
  }
  if (quotaCount) {
    parts.push(
      `${quotaCount} ${quotaCount === 1 ? "limite de uso" : "limites de uso"}`,
    );
  }
  return parts.join(" · ");
};

// Expand-all / collapse-all over every module that has detail to show.
const expandableKeys = computed(() =>
  (catalog.value?.modules ?? []).filter(hasDetails).map((m) => m.key),
);
const allExpanded = computed(
  () =>
    expandableKeys.value.length > 0 &&
    expandableKeys.value.every((key) => openPanels.value.includes(key)),
);
const toggleAll = () => {
  openPanels.value = allExpanded.value ? [] : [...expandableKeys.value];
};

// Module header status. core modules are always entitled (implicit grant). For
// add_on modules there's no server effective_state anymore: read trial/expiry
// off the module's CORE features only — those cascade from the master grant, so
// they reflect the module's own lifecycle. Add-ons carry independent grants
// (their own trial/expiry) and must not sway the module header's status.
const moduleStatus = (mod) => {
  if (mod.kind === "core") return "entitled";
  if (!mod.granted) return "locked";
  if (mod.state === "suspended") return "suspended";
  const feats = mod.features;
  if (feats.some((f) => f.state === "trialing")) return "trialing";
  if (feats.length && feats.every((f) => f.state === "expired")) return "expired";
  return "entitled";
};

// A module's features can only be managed once the module is reachable —
// active or in trial. For any other state (locked/suspended/expired) the
// feature controls are disabled; the module must be granted first.
const isModuleActive = (mod) => {
  const status = moduleStatus(mod);
  return status === "entitled" || status === "trialing";
};

// Trial expiry as a muted caption under the header. The module kind now lives
// in its own chip beside the title, so it's no longer repeated here.
const moduleMeta = (mod) => {
  if (mod.trial_expires_at) {
    return `Trial expira ${formatFromNow(mod.trial_expires_at)}`;
  }
  return "";
};

// ---- Error mapping ----

const mutationErrorMessage = (e) => {
  // Surface the backend's error as-is. `detail` is a string for most errors and
  // an array of validation items for 422 (FastAPI).
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || e?.message;
  return detail || e?.message || "Ocorreu um erro.";
};

const runMutation = async (key, fn, successMessage) => {
  pendingKey.value = key;
  try {
    await fn();
    snackbar.success(successMessage);
    recentKey.value = key;
    clearTimeout(recentKeyTimer);
    recentKeyTimer = setTimeout(() => {
      recentKey.value = null;
    }, 2000);
  } catch (e) {
    snackbar.error(mutationErrorMessage(e));
  } finally {
    pendingKey.value = null;
  }
};

// ---- Module actions ----

const grantModule = (mod) => {
  return runMutation(
    `module:${mod.key}`,
    () => moduleGrantMutation.mutateAsync({ moduleKey: mod.key, body: {} }),
    `Módulo ${mod.title} concedido.`,
  );
};

const resumeModule = (mod) => {
  return runMutation(
    `module:${mod.key}`,
    () => moduleResumeMutation.mutateAsync(mod.key),
    `Módulo ${mod.title} reativado.`,
  );
};

const confirmSuspendModule = (mod) => {
  confirmDialog.value = {
    open: true,
    title: `Suspender ${mod.title}`,
    message:
      "A suspensão bloqueia todas as funcionalidades deste módulo para o cliente (bloqueio de cobrança). Deseja continuar?",
    actionLabel: "Suspender",
    color: "warning",
    action: () =>
      runMutation(
        `module:${mod.key}`,
        () => moduleSuspendMutation.mutateAsync(mod.key),
        `Módulo ${mod.title} suspenso.`,
      ),
  };
};

const confirmRevokeModule = (mod) => {
  confirmDialog.value = {
    open: true,
    title: `Revogar ${mod.title}`,
    message:
      "A revogação remove a concessão do módulo e bloqueia todas as suas funcionalidades. Deseja continuar?",
    actionLabel: "Revogar",
    color: "error",
    action: () =>
      runMutation(
        `module:${mod.key}`,
        () =>
          moduleGrantMutation.mutateAsync({
            moduleKey: mod.key,
            body: { enabled: false },
          }),
        `Módulo ${mod.title} revogado.`,
      ),
  };
};

// ---- Feature actions ----

// locked_on features are switched via their module master (direct writes 409)
const grantOwningModule = (feature) => {
  const mod = catalog.value?.modules.find((m) => m.key === feature.module);
  if (!mod) return;
  return grantModule(mod);
};

// Features are bool-only now, so granting just enables them — no value dialog.
const grantFeature = (feature) => {
  return runMutation(
    `feature:${feature.feature}`,
    () =>
      featureGrantMutation.mutateAsync({
        flagKey: feature.feature,
        body: { value: true },
      }),
    "Funcionalidade concedida.",
  );
};

const resumeFeature = (feature) => {
  return runMutation(
    `feature:${feature.feature}`,
    () => featureResumeMutation.mutateAsync(feature.feature),
    "Funcionalidade reativada.",
  );
};

const confirmSuspendFeature = (feature) => {
  confirmDialog.value = {
    open: true,
    title: "Suspender funcionalidade",
    message: `"${feature.description}" ficará bloqueada para o cliente até a reativação. Deseja continuar?`,
    actionLabel: "Suspender",
    color: "warning",
    action: () =>
      runMutation(
        `feature:${feature.feature}`,
        () => featureSuspendMutation.mutateAsync(feature.feature),
        "Funcionalidade suspensa.",
      ),
  };
};

const confirmRevokeFeature = (feature) => {
  confirmDialog.value = {
    open: true,
    title: "Revogar funcionalidade",
    message: `A concessão própria de "${feature.description}" será removida. Deseja continuar?`,
    actionLabel: "Revogar",
    color: "error",
    action: () =>
      runMutation(
        `feature:${feature.feature}`,
        () =>
          featureGrantMutation.mutateAsync({
            flagKey: feature.feature,
            body: { enabled: false },
          }),
        "Funcionalidade revogada.",
      ),
  };
};

// ---- Quotas ----

const openQuotaDialog = (quota) => {
  quotaDialog.value = {
    open: true,
    quota,
    value: quota.limit,
    showErrors: false,
  };
};

const submitQuotaSet = () => {
  quotaDialog.value.showErrors = true;
  if (quotaValueErrors.value.length) return;

  const { quota, value } = quotaDialog.value;
  quotaDialog.value.open = false;

  runMutation(
    `quota:${quota.key}`,
    () =>
      quotaSetMutation.mutateAsync({
        quotaKey: quota.key,
        body: { limit: Number(value) },
      }),
    "Limite atualizado.",
  );
};

// Resetting to the registry default is non-destructive (re-settable), so it runs
// without a confirm dialog — unlike module/feature revoke.
const resetQuota = (quota) => {
  return runMutation(
    `quota:${quota.key}`,
    () => quotaResetMutation.mutateAsync(quota.key),
    "Limite restaurado para o padrão.",
  );
};

// Triggered from the edit dialog; close it first so the quota row's progress
// bar is the only thing animating during the reset.
const submitQuotaReset = () => {
  const { quota } = quotaDialog.value;
  quotaDialog.value.open = false;
  resetQuota(quota);
};

// ---- Trials ----

const openTrialDialog = (targetType, targetKey, label) => {
  trialDialog.value = {
    open: true,
    targetType,
    targetKey,
    label,
    expiresAt: null,
    startsAt: startOfDay(new Date()),
    showErrors: false,
  };
};

const submitTrial = () => {
  trialDialog.value.showErrors = true;
  if (trialExpiresErrors.value.length || trialStartsErrors.value.length) {
    return;
  }

  const { targetType, targetKey, expiresAt, startsAt } = trialDialog.value;

  // Date-only inputs: the trial owns the full local day, so expiry resolves to
  // end-of-day and start to start-of-day. toISOString() yields the tz-aware (Z)
  // ISO 8601 the backend requires; naive datetimes are rejected with 422.
  const body = { trial_expires_at: endOfDay(expiresAt).toISOString() };
  if (startsAt) {
    body.trial_starts_at = startOfDay(startsAt).toISOString();
  }

  trialDialog.value.open = false;

  if (targetType === "module") {
    runMutation(
      `module:${targetKey}`,
      () => moduleGrantMutation.mutateAsync({ moduleKey: targetKey, body }),
      "Trial do módulo iniciado.",
    );
  } else {
    runMutation(
      `feature:${targetKey}`,
      () => featureGrantMutation.mutateAsync({ flagKey: targetKey, body }),
      "Trial da funcionalidade iniciado.",
    );
  }
};

// ---- Plans ----

// Plan grants are bare keys now (no "module:" prefix): a grant key is either a
// module key or a feature key. Resolve module keys to their human title.
const planModule = (grant) =>
  catalog.value?.modules.find((m) => m.key === grant);

const grantLabel = (grant) => {
  const mod = planModule(grant);
  return mod ? `Módulo ${mod.title}` : grant;
};

const grantIcon = (grant) => (planModule(grant) ? mdiViewModule : mdiFlag);

const confirmApplyPlan = (plan) => {
  confirmDialog.value = {
    open: true,
    title: `Aplicar plano ${plan.plan_key}`,
    message: `O plano concede: ${plan.grants.map(grantLabel).join(", ")}. Deseja continuar?`,
    actionLabel: "Aplicar",
    color: "primary",
    action: () =>
      runMutation(
        `plan:${plan.plan_key}`,
        () => planApplyMutation.mutateAsync({ planKey: plan.plan_key }),
        `Plano ${plan.plan_key} aplicado.`,
      ),
  };
};

// ---- Shared dialog plumbing ----

const runConfirmedAction = () => {
  const action = confirmDialog.value.action;
  confirmDialog.value.open = false;
  if (action) action();
};

// Surface catalog load errors
watch(error, (err) => {
  if (err) {
    snackbar.error(mutationErrorMessage(err));
  }
});
</script>

<style scoped>
/* Modules are native expansion panels (accordion variant): header always
   visible, the feature/quota detail tucked behind it. The panel-text wrapper's
   default side padding is dropped so the feature and quota rows keep their own
   px-4 and line up under the panel title's px-4; a little bottom padding stands
   in for the gap the wrapper used to give. */
.modules :deep(.v-expansion-panel-text__wrapper) {
  padding: 0 0 8px;
}

/* A small gap separates each module so they read as distinct sections rather
   than one continuous block. The `flat` prop already drops the panel shadow and
   the accordion's connecting hairline; the accordion variant keeps panel
   margins static, so this gap doesn't shift when a panel opens. */
.modules :deep(.v-expansion-panel:not(:first-child)) {
  margin-top: 12px;
}

/* Reserved trailing gutter — the width of the 28px comfortable icon button — so
   the module status chip lands at the same x as the feature-row chips below,
   whether the module carries the overflow menu (add_on) or no controls (core).
   Mirrors .feature-actions in EntitlementFeatureList. */
.module-actions {
  min-width: 28px;
}

/* Pending bar for menu-initiated module mutations, pinned to the panel's top
   edge — the v-expansion-panel is the positioning context. */
.module-progress {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

/* Flash overlay (instead of animating background-color) so the surface keeps
   its own color underneath. */
.recently-changed {
  position: relative;
}

.recently-changed::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background-color: rgb(var(--v-theme-primary));
  opacity: 0;
  animation: entitlement-flash 1.6s ease-out;
}

@keyframes entitlement-flash {
  from {
    opacity: 0.12;
  }
  to {
    opacity: 0;
  }
}
</style>
